import { EVENT_TYPE } from '@hedepy/common/enums/EVENT_TYPE'
import { SCHEDULE_SLOT_TYPE } from '@hedepy/common/enums/SCHEDULE_SLOT_TYPE'
import { useMemo } from 'react'

import useRebookEvent from '@/legacy-src/components/reservation/hooks/useRebookEvent'
import useFreeSlots, { type UseFreeSlots } from '@/legacy-src/hooks/useFreeSlots'

import { FreeSlotTypeFilter, OFFLINE_NOT_AVAILABLE } from '@/src/domains/client-booking/constants'
import type { FreeSlotTypeFilterWithAvailability } from '@/src/domains/client-booking/types'

const slotTypeByEventType: Record<EVENT_TYPE, SCHEDULE_SLOT_TYPE> = {
  [EVENT_TYPE.ONLINE]: SCHEDULE_SLOT_TYPE.ONLINE,
  [EVENT_TYPE.OFFLINE]: SCHEDULE_SLOT_TYPE.OFFLINE,
}

export interface UseClientBookingFreeSlots extends UseFreeSlots {
  type: FreeSlotTypeFilterWithAvailability
}

export const useClientBookingFreeSlots = ({ type, ...rest }: UseClientBookingFreeSlots) => {
  const { slots, isLoading, refetchSlots } = useFreeSlots(rest)

  const { originalEventData } = useRebookEvent()

  const filteredSlots = useMemo(() => {
    return slots
      ?.filter((slot) => {
        switch (type) {
          case FreeSlotTypeFilter.ONLINE: {
            return slot.type === SCHEDULE_SLOT_TYPE.ONLINE || slot.type === SCHEDULE_SLOT_TYPE.HYBRID
          }
          case FreeSlotTypeFilter.OFFLINE: {
            return slot.type === SCHEDULE_SLOT_TYPE.OFFLINE || slot.type === SCHEDULE_SLOT_TYPE.HYBRID
          }
          case OFFLINE_NOT_AVAILABLE: {
            return slot.type === SCHEDULE_SLOT_TYPE.ONLINE || slot.type === SCHEDULE_SLOT_TYPE.HYBRID
          }
          default: {
            return true
          }
        }
      })
      .filter((slot) =>
        originalEventData?.type !== undefined
          ? slot.type === slotTypeByEventType[originalEventData.type] || slot.type === SCHEDULE_SLOT_TYPE.HYBRID
          : true,
      )
      .map((slot) => {
        // When the offline therapy is not available, we want to treat HYBRID slots as ONLINE
        if (type === OFFLINE_NOT_AVAILABLE) {
          return {
            ...slot,
            type: SCHEDULE_SLOT_TYPE.ONLINE,
          }
          // We fake the type so the HYBRID slots look like the original event (because user cannot select different type when rebooking)
        } else if (originalEventData?.type !== undefined && slot.type === SCHEDULE_SLOT_TYPE.HYBRID) {
          return {
            ...slot,
            type: slotTypeByEventType[originalEventData.type],
          }
        }
        return slot
      })
  }, [slots, originalEventData?.type, type])

  return { slots: filteredSlots, allSlots: slots, isLoading, refetchSlots }
}
